import {Component, Inject, OnInit} from '@angular/core';
import {
    faBan,
    faFlagCheckered,
    faForward,
    faGlobe,
    faHandPaper,
    faQuestionCircle,
    faRunning,
    faSyncAlt,
    faTasks,
    faThumbsDown,
    faThumbsUp,
    faTv,
    faTimesCircle,
    faHourglass,
    faTrash, faHourglassHalf, faCheckCircle,
} from '@fortawesome/pro-solid-svg-icons';
import {faChevronDown} from '@fortawesome/pro-light-svg-icons';
import {
    ActivityExecutionStatus,
    AssessmentEvaluationStatus,
    AssessmentTask
} from '@webmate-web-core/shared/services/audit.service';
import {
    AuditActivityId,
    AuditId,
    FindingId,
    ProjectId,
    SpecId,
    TestObjectId
} from '@webmate-web-core/shared/types/ids';
import {BehaviorSubject, of, Subscription} from 'rxjs';
import {WorkbenchAuditService} from '../../../services/workbench-audit.service';
import {Spec, Finding, ObservationTarget, TestMgmtEventTypes} from '@webmate-web-core/shared/types/testmgmt';
import {AuditInfo, AuditSession} from '../../../services/audit-session';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {Tag} from '@webmate-web-core/shared/types/tag';
import {InspectorService} from '../../../services/inspector.service';
import {WorkbenchToolService} from '../../../services/workbench-tool.service';
import {
    InspectorEvent,
    InspectorImplementation,
    OpenAuditActivityEvent,
    OpenFindingForActivityEvent
} from '../../../types';
import {WorkbenchContextService} from '../../../services/workbench-context.service';
import {filter, map, switchMap, timeout} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BrowserSessionInfo} from '@webmate-web-core/shared/types/browsersession';
import {BrowserSessionService} from '@webmate-web-core/shared/services/browsersession.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {PersistenceService} from '@webmate-web-core/shared/services/persistence.service';
import {DeployableService} from '@webmate-web-core/shared/services/device/deployable.service';
import {WorkbenchConsoleService} from '../../../services/workbench-console.service';
import {TimeoutError} from 'rxjs';
import {INTERACTION_PATH_OPENER, InteractionPathOpener} from '@webmate-web-core/shared/services/interactionpath-opener.service';

@UntilDestroy()
@Component({
    selector: 'wm-audit-inspector',
    templateUrl: './audit-inspector.component.html',
    styleUrls: ['./audit-inspector.component.less'],
})
export class AuditInspectorComponent implements OnInit, InspectorImplementation {
    protected readonly faQuestionCircle = faQuestionCircle;
    protected readonly faTimesCircle = faTimesCircle;
    protected readonly faGlobe = faGlobe;
    protected readonly faChevronDown = faChevronDown;
    protected readonly faThumbsUp = faThumbsUp;
    protected readonly faThumbsDown = faThumbsDown;
    protected readonly faForward = faForward;
    protected readonly faTv = faTv;
    protected readonly faBan = faBan;
    protected readonly faRunning = faRunning;
    protected readonly faFlagCheckered = faFlagCheckered;
    protected readonly faTasks = faTasks;
    protected readonly faHandPaper = faHandPaper;
    protected readonly faSyncAlt = faSyncAlt;
    protected readonly faTrash = faTrash;


    auditSession: AuditSession;
    auditInfo: AuditInfo;
    projectId: ProjectId;
    auditId?: AuditId;
    task?: AssessmentTask;
    activeTaskId = new BehaviorSubject<AuditActivityId | undefined>(undefined);
    activeFindingId?: FindingId;
    specLabels: Tag[];
    findingsForActiveActivity: FindingId[] = [];
    findingToOpen?: FindingId;

    availableSpecs: SpecId[];

    private auditInfoSubscription?: Subscription;

    private _tabViewIndex: number = 0;

    evaluationStatusOptions = {
        'passed': {label: 'Passed', value: 'passed', icon: faCheckCircle},
        'failed': {label: 'Failed', value: 'failed', icon: faTimesCircle},
        'notapplicable': {label: 'Not Applicable', value: 'notapplicable', icon: faBan},
        'undecided': {label: 'Undecided', value: 'undecided', icon: faQuestionCircle},
    };
    evaluationStatusArray = Object.values(this.evaluationStatusOptions);
    currentEvaluationStatus: AssessmentEvaluationStatus;

    executionStatusOptions = {
        'notstarted': {label: 'Not started', value: 'notstarted', icon: faTasks},
        'inprogress': {label: 'In progress', value: 'inprogress', icon: faRunning},
        'finished': {label: 'Finished', value: 'finished', icon: faHourglassHalf},
        'blocked': {label: 'Blocked', value: 'blocked', icon: faHandPaper},
        'waitingforreview': {label: 'Waiting for review', value: 'waitingforreview', icon: faFlagCheckered},
        'skipped': {label: 'Skipped', value: 'skipped', icon: faForward},
    };
    executionStatusArray = Object.values(this.executionStatusOptions);
    currentExecutionStatus: ActivityExecutionStatus;

    constructor(private readonly workbenchContextService: WorkbenchContextService,
                private readonly workbenchAuditService: WorkbenchAuditService,
                private readonly browserSessionService: BrowserSessionService,
                private readonly deployableService: DeployableService,
                private readonly workbenchConsoleService: WorkbenchConsoleService,
                private readonly testService: TestService,
                private readonly workbenchToolService: WorkbenchToolService,
                private readonly inspectorService: InspectorService,
                private readonly persistenceService: PersistenceService,
                @Inject(INTERACTION_PATH_OPENER) private readonly interactionPathOpener: InteractionPathOpener,
                private notificationService: WmNotificationService) {
    }

    ngOnInit() {
        this.projectId = this.workbenchContextService.getCurrentWorkbenchContext()?.project!;
        this.tabViewIndex = Number(this.persistenceService.getItem('wm.workbench.audit-inspector.activeTab') || 0);

        this.workbenchContextService.subscribeWorkbenchContext$().pipe(untilDestroyed(this)).subscribe(context => {
            const auditContext = context.auditContext;
            this.activeFindingId = auditContext?.activeFinding;

            const auditId = auditContext?.activeAudit;
            if (auditId && auditId !== this.auditId) {
                // (Re)bind to the active audit. This inspector instance is reused when the workbench switches
                // audits in place (the inspector item stays 'Audit', so the component is not recreated), so we
                // must re-acquire the session and reload here; otherwise it keeps querying the previous audit.
                this.bindToActiveAudit(auditId);
            }

            if (auditContext?.activeAuditActivity && auditContext.activeAuditActivity.id !== this.activeTaskId.value) {
                this.openActivity(auditContext.activeAuditActivity.id);
            }
        });

        this.inspectorService.switchToFindingTabSubject$().pipe(untilDestroyed(this)).subscribe(() => {
            this.tabViewIndex = 1;
        });

        this.testService.getFindingUpdateEventsForProject(this.projectId).pipe(untilDestroyed(this)).subscribe(e => {
            if (e.eventType === TestMgmtEventTypes.FindingAdded) {
                const activeTargetId = this.getTaskTargetId(this.task);
                const newFindingIds = e.findings
                    .filter((f: Finding) => !!activeTargetId && f.affectedSpecs?.includes(this.task!.assessedSpec) && f.observationTargets?.some(target => target.testObjectId === activeTargetId))
                    .map((f: Finding) => f.id)
                    .filter((id: FindingId) => !this.findingsForActiveActivity.includes(id)); // may already be present from optimistic insert
                this.findingsForActiveActivity = this.findingsForActiveActivity.concat(newFindingIds);
            } else if (e.eventType === TestMgmtEventTypes.FindingDeleted) {
                const deletedFindings = e.findingIds as FindingId[];
                this.findingsForActiveActivity = this.findingsForActiveActivity.filter(f => !deletedFindings.includes(f))
            }
        });
    }

    private bindToActiveAudit(auditId: AuditId): void {
        this.auditId = auditId;
        this.auditSession = this.workbenchAuditService.getOrCreateAuditSession(auditId);
        this.task = undefined;
        this.activeTaskId.next(undefined);

        // Subscribe to changes for the (new) audit session. Tear down the previous binding first.
        this.auditInfoSubscription?.unsubscribe();
        this.auditInfoSubscription = this.auditSession.getAuditInfo$().pipe(
            switchMap((auditInfo: AuditInfo) => {
                return this.activeTaskId.asObservable().pipe(map(activeTaskId => ({auditInfo, activeTaskId})));
            }),
            switchMap(({auditInfo, activeTaskId}) => {
                if (!activeTaskId) {
                    return of({findings: [] as FindingId[], auditInfo, activeTaskId});
                }
                return this.auditSession.getFindingIdsForActivity(activeTaskId).pipe(map(findings => ({findings, auditInfo, activeTaskId})));
            }),
            untilDestroyed(this)
        ).subscribe(({auditInfo, findings, activeTaskId}) => {
            this.findingsForActiveActivity = findings || [];
            if (this.findingToOpen) {
                this.openFinding(this.findingToOpen);
                this.findingToOpen = undefined;
            }
            this.auditInfo = auditInfo;
            this.availableSpecs = Object.keys(this.auditInfo.specs || {});
            if (activeTaskId) {
                this.applyActiveActivity(activeTaskId);
            }
        });
    }

    get tabViewIndex(): number {
        return this._tabViewIndex;
    }

    set tabViewIndex(value: number) {
        this._tabViewIndex = value;
        this.persistenceService.setItem('wm.workbench.audit-inspector.activeTab', this._tabViewIndex.toString());
    }

    openTestObject(testObjectId?: TestObjectId) {
        const interactionPath = testObjectId ? this.auditInfo.statesByTestObjectId?.[testObjectId]?.provenance?.path : undefined;
        if (!interactionPath) {
            return;
        }

        this.interactionPathOpener.open({interactionPath});
    }

    private openActivity(activityId: AuditActivityId, findingId?: FindingId) {
        this.activeTaskId.next(activityId);
        this.applyActiveActivity(activityId);
        this.findingToOpen = findingId;
    }

    private applyActiveActivity(activityId: AuditActivityId): void {
        this.task = this.auditInfo?.idToAssessmentTask?.[activityId];
        this.currentExecutionStatus = this.task?.executionStatus || 'notstarted';
        this.currentEvaluationStatus = this.task?.evaluationStatus || 'undecided';

        const connectedSpec: Spec | undefined = this.task ? this.auditInfo?.specs?.[this.task.assessedSpec] : undefined;
        this.specLabels = connectedSpec?.labels || [];
        this.workbenchToolService.changeRelatedTools(connectedSpec?.labels);
    }

    private openFinding(findingId: FindingId) {
        if (findingId) {
            this.activeFindingId = findingId;
            this.tabViewIndex = 1;
        }
    }

    setAssessmentTaskEvaluationStatus(status: AssessmentEvaluationStatus) {
        this.auditSession.setAssessmentTaskEvaluationStatus(this.task!.id, status);
    }

    setAssessmentTaskExecutionStatus(status: ActivityExecutionStatus) {
        this.auditSession.setAssessmentTaskExecutionStatus(this.task!.id, status);
    }

    setAssessmentTaskSummary(summary: string) {
        this.auditSession.setAssessmentTaskSummary(this.task!.id, summary);
    }

    get activeFindingTargets(): ObservationTarget[] {
        const activeTarget = this.getActiveTaskTarget()?.target;
        return activeTarget ? [activeTarget] : [];
    }

    onFindingCreated(finding: Finding): void {
        // Show the new finding immediately instead of waiting for the FindingAdded event round-trip.
        if (!this.findingsForActiveActivity.includes(finding.id)) {
            this.findingsForActiveActivity = [...this.findingsForActiveActivity, finding.id];
        }
        this.workbenchContextService.setContextActiveFinding(finding.id);
    }

    handleInspectorEvent(event: InspectorEvent): void {
        switch (event.type) {
            case 'SwitchToFindingTab': {
                this.tabViewIndex = 1;
                break;
            }

            case 'OpenAuditActivity': {
                const openAuditActivityEvent = event as OpenAuditActivityEvent;
                this.openActivity(openAuditActivityEvent.activityID);
                break;
            }

            case 'OpenFindingForActivityEvent': {
                const typedEvent = event as OpenFindingForActivityEvent;
                this.openActivity(typedEvent.activityId, typedEvent.findingId);
                break;
            }
        }
    }

    isOpenAuditDisabled() {
        return !this.getTaskTargetId(this.task) || !this.auditInfo.statesByTestObjectId?.[this.getTaskTargetId(this.task)!]?.provenance?.path
    }

    getTaskTargetId(task?: AssessmentTask): TestObjectId | undefined {
        return task?.testObjectId;
    }

    getActiveTaskTargetName(): string {
        const targetId = this.getTaskTargetId(this.task);
        return (targetId ? this.auditInfo.testObjectsById?.[targetId]?.target?.name : undefined)
            || (targetId ? this.auditInfo.statesByTestObjectId?.[targetId]?.name : undefined)
            || 'Unknown target';
    }

    private getActiveTaskTarget() {
        const targetId = this.getTaskTargetId(this.task);
        return targetId ? this.auditInfo.testObjectsById?.[targetId] : undefined;
    }

    protected readonly faHourglass = faHourglass;
}
